import { z } from "zod";
import { configFromEnv } from "@utils/config";
//#region src/index.client.ts
var config = { loginConnectorUrl: configFromEnv.asString("LOGIN_CONNECTOR_API") };
var userData;
/**
* public interface for the Gateway endpoint, which provides convenience
* methods to streamline integrations.
*/
var gGateway = Object.assign(function intercept(inputUrl) {
	if (gGateway.disabled) return inputUrl;
	const url = new URL(inputUrl);
	if (gGateway.forceTrailingSlash && !url.pathname.endsWith("/")) url.pathname += "/";
	return (gGateway.app === void 0 ? gGateway.endpoint : "http://localhost/__internal__") + "/" + (url.protocol === "https:" ? "" : url.protocol) + url.hostname + (url.port ? `:${url.port}` : "") + url.pathname + url.search;
}, {
	/**
	* disable Gateway interception of URLs (which can be useful in certain
	* environments, e.g. if some requests are being run serverside where auth
	* is already provided).
	*
	* @default false
	*/
	disabled: false,
	/**
	* configuration of the base url for the endpoint that hosts the Gateway
	* application, which supports providing authorization for user requests.
	*
	* @default "/api/gateway"
	*/
	endpoint: "/api/gateway",
	/**
	* if enabled, the gateway force re-writes the all API urls to include a
	* trailing slash in order to avoid redirects (especially in Next.js
	* applications with trailingSlash: true setting)
	*/
	forceTrailingSlash: false,
	/**
	* High-level implementation of a fetch wrapper, which automatically routes
	* given requests through the Gateway.
	*/
	fetch: (input, init) => {
		let req;
		const prevForceTrailingSlash = gGateway.forceTrailingSlash;
		gGateway.forceTrailingSlash = gGateway.app ? false : prevForceTrailingSlash;
		if (input instanceof Request) req = new Request(gGateway(input.url), input);
		else if (input instanceof URL) req = new Request(gGateway(input.toString()), init);
		else req = new Request(gGateway(input), init);
		gGateway.forceTrailingSlash = prevForceTrailingSlash;
		return !gGateway.app ? fetch(req) : gGateway.app.fetch(req);
	},
	/**
	* If specified, then requests triggered through gGateway.fetch will be
	* routed directly to this Hono application instance to save HTTP
	* roundtrips in server-based environments.
	*/
	app: void 0,
	/**
	* utility to perform user authorization, first checking if the user has an
	* active session within LoginConnector and secondarily
	*
	* @note This method depends on environment variable "LOGIN_CONNECTOR_API"
	*       or "NEXT_PUBLIC__LOGIN_CONNECTOR_API" being injected at build time,
	*       otherwise the authorization will fail.
	*
	* @param clientName - specify the clientName to use when fetching auth
	*                     token from LoginConnector
	*/
	async authenticateUser(clientName, _loginConnectorToken) {
		try {
			const loginConnectorToken = _loginConnectorToken || await (async () => {
				const loginConnectorRes = await fetch(`${await config.loginConnectorUrl.get()}/api/GetAuthenticationToken/Get?clientName=${encodeURIComponent(clientName)}`, { credentials: "include" });
				if (!loginConnectorRes.ok) throw new Error("LoginConnector network error");
				return authTokenSchema.parse(await loginConnectorRes.json()).AuthenticationToken;
			})();
			if (!loginConnectorToken) return gGateway.authenticateGuest();
			const url = (() => {
				const url = gGateway.endpoint + "/auth/user";
				return gGateway.forceTrailingSlash ? url + "/" : url;
			})();
			const res = await fetch(url, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					clientName,
					token: loginConnectorToken
				})
			});
			if (!res.ok) throw new Error("Gateway network error");
			userData = {
				...userDataSchema.parse(await res.json()),
				loginConnectorToken
			};
			return userData;
		} catch (err) {
			console.error("gGateway.authenticateUser", "unable to log user in, falling back to guest token", { err });
			return await gGateway.authenticateGuest();
		}
	},
	/**
	* utility method to authenticate the user as a guest, without first
	* determining his login status within LoginConnector.
	*
	* the primary purpose of this method is to facilitate the initial
	* authentication handshake with the Gateway endpoint while login is
	* disabled due to operational issues.
	*/
	async authenticateGuest() {
		const url = (() => {
			const url = gGateway.endpoint + "/auth/guest";
			return gGateway.forceTrailingSlash ? url + "/" : url;
		})();
		await fetch(url);
		return userData = {
			type: "guest",
			provider: null,
			id: null,
			institutionId: null,
			loginConnectorToken: void 0
		};
	},
	/**
	* allow external applications to access the latest userData extracted from
	* the authentication process.
	*/
	getUserData() {
		return userData;
	},
	/**
	* create a forward link, which will propagate the user to the desired URL
	* and apply relevant authorization params.
	*/
	forward(inputUrl, _authz) {
		try {
			const url = new URL(inputUrl);
			const authz = _authz ?? (() => {
				switch (url.hostname) {
					case "componentframework.gyldendal.dk":
					case "qa-componentframework.gyldendal.dk":
					case "test-componentframework.gyldendal.dk": return "componentframework";
				}
			})();
			if (!authz) return inputUrl;
			return gGateway.endpoint + `/forward/${encodeURIComponent(authz)}/` + (url.protocol === "https:" ? "" : url.protocol) + url.hostname + (url.port ? `:${url.port}` : "") + url.pathname + url.search;
		} catch {
			return inputUrl;
		}
	}
});
var authTokenSchema = z.object({ AuthenticationToken: z.string() });
var userDataSchema = z.object({
	type: z.enum(["teacher", "student"]),
	provider: z.enum([
		"IP",
		"UNIC",
		"EKEY",
		"UNKNOWN"
	]),
	id: z.string(),
	institutionId: z.string().nullish().transform((it) => it ?? null)
});
globalThis.gGateway ??= gGateway;
var gGatewayExport = globalThis.gGateway;
//#endregion
export { gGatewayExport as gGateway };

//# sourceMappingURL=index.client.js.map